import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { config } from "../../config/app-config";
import { ROUTES } from "../../routes/paths";
import { onAuthRoute } from "./auth-route";
import { onAuthExpired, stashReturnPath } from "../../hermesclient/client";
import { errorMessage } from "../../utils/error-message";

/**
 * AuthExpiredDialog — the re-login prompt for FEATURE-20260715-browser-auth-expiry
 * -relogin. When a browser tab wakes past the auth-token TTL, the mailbox re-mint
 * gets a 401/403 (AuthExpiredError) and the hermes client fires onAuthExpired().
 * This dialog is the app's listener: instead of yanking the tab to /auth/login
 * with no warning, it tells the user their session expired and lets them click to
 * sign in.
 *
 * Why a dialog and not true in-place re-auth: the login flow is a full-page
 * provider handoff (login-page.tsx `startLogin` -> window.location -> Google/etc.
 * -> /auth/handoff), so the provider round-trip navigates away by construction —
 * zero-navigation re-auth isn't possible. The win here is AWARENESS + CONTROL: the
 * user chooses to re-auth rather than being silently redirected, and the return
 * path + persisted tabUid bring them back to the same page as the same tab.
 *
 * Reuses the exact login mechanism (badges from /api/auth/providers + the
 * redirect-to-provider startLogin) so there is one auth path, not two.
 */

interface Badge {
  id: string;
  label: string;
  iconUrl: string;
  loginUrl: string;
}

/**
 * True when the browser is already sitting on an auth route (/auth/login,
 * /auth/handoff). Read from window.location rather than useLocation() because
 * this dialog is mounted as a SIBLING of <AppRoutes/> (App.tsx), and the
 * BrowserRouter lives inside it (routes/route.tsx) — so there is no router
 * context here and the hook would throw.
 *
 * basename is a deploy knob (config.router.basename), and location.pathname
 * includes it, so strip it before comparing against the ROUTES constants.
 */

export default function AuthExpiredDialog() {
  const [open, setOpen] = useState(false);
  const [badges, setBadges] = useState<Badge[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Register the transport listener once; open the dialog when auth expires.
  //
  // Suppressed on the auth routes themselves. The dialog is mounted app-wide, so
  // it also fired on /auth/login — where its only escape hatch ("Go to sign-in
  // page") assigns the URL the user is ALREADY on, re-rendering the same
  // non-dismissable dialog. That is an inescapable loop, and it triggers on a
  // plain first visit with no session at all, not just on token expiry: the WS
  // createMailbox 401s either way. There is nothing to prompt about on the
  // sign-in page — the page IS the prompt. (BUG-20260725-auth-expired-dialog
  // -login-loop)
  useEffect(() => onAuthExpired(() => {
    if (onAuthRoute()) return;
    setOpen(true);
  }), []);

  // Load providers lazily, only when the dialog first opens.
  useEffect(() => {
    if (!open || badges !== null) return;
    (async () => {
      try {
        const res = await fetch(`${config.hermes.url}/api/auth/providers`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setBadges(data.badges ?? []);
      } catch (err) {
        setLoadError(errorMessage(err) || String(err));
        setBadges([]);
      }
    })();
  }, [open, badges]);

  const startLogin = (badge: Badge) => {
    // Same handoff the login page uses; stash where we are so re-auth returns here.
    stashReturnPath();
    const frontendUrl = `${window.location.origin}${ROUTES.AUTH.HANDOFF}`;
    const sep = badge.loginUrl.includes("?") ? "&" : "?";
    window.location.href = `${config.hermes.url}${badge.loginUrl}${sep}redirect=${encodeURIComponent(
      frontendUrl
    )}`;
  };

  // Fallback: if providers can't load, offer the full login page rather than a
  // dead dialog. Belt-and-braces against the loop above: assigning the URL we are
  // already on is a no-op that leaves the dialog stuck open, so reload instead.
  const goToLoginPage = () => {
    stashReturnPath();
    if (onAuthRoute()) window.location.reload();
    else window.location.assign(ROUTES.AUTH.LOGIN);
  };

  return (
    <Dialog
      open={open}
      // Not dismissable: the session is dead, there is nothing behind it to use
      // until they re-auth, so onClose is a no-op (backdrop/escape do nothing).
      onClose={() => {}}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Session expired</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Your sign-in has expired. Sign in again to continue — you’ll return to
          this page.
        </DialogContentText>
        {badges === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : badges.length === 0 ? (
          <Alert severity="warning" variant="outlined">
            No sign-in providers are available.
            {loadError && (
              <>
                <br />
                <code>{loadError}</code>
              </>
            )}
          </Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {badges.map((b) => (
              <Button
                key={b.id}
                fullWidth
                variant="outlined"
                startIcon={
                  b.iconUrl ? (
                    <img
                      src={b.iconUrl}
                      alt=""
                      style={{ width: 20, height: 20 }}
                    />
                  ) : undefined
                }
                onClick={() => startLogin(b)}
              >
                {b.label}
              </Button>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={goToLoginPage}>Go to sign-in page</Button>
      </DialogActions>
    </Dialog>
  );
}
